import { useState } from "react";
import { useApp } from "../context/AppContext";
import { getApiUrl } from "../config/api";
import {
  notifyError,
  notifyInfo,
  notifySuccess,
  notifyWarning,
} from "../utils/notify";
import type { AppointmentScheduledMessage } from "../types/appointmentScheduledMessages";
import { getScheduledMessageDisplayName } from "../utils/appointmentScheduledMessageDisplay";

// Subset of POST /scheduled-messages/{uuid}/send the handler returns.
type SendNowResult = Pick<
  AppointmentScheduledMessage,
  "uuid" | "status" | "failure_reason"
>;

export interface AppointmentScheduledMessageActions {
  /** uuid of the row with an action in flight (drives its spinner), else null. */
  actionLoading: string | null;
  sendNow: (msg: AppointmentScheduledMessage) => Promise<void>;
  cancel: (msg: AppointmentScheduledMessage) => Promise<void>;
  retry: (msg: AppointmentScheduledMessage) => Promise<void>;
}

/**
 * Send-now / cancel / retry for an appointment scheduled message, reusing the
 * existing compliance endpoints keyed by the message's OWN ``transaction_uuid``.
 * Shared so the two-way chat "Scheduled messages" drawer drives the exact same
 * backend as the Transaction Modal — no duplicated action endpoints. The
 * ``onChanged`` callback is awaited after each mutation to refresh the caller's
 * list.
 */
export function useAppointmentScheduledMessageActions(
  onChanged: () => void | Promise<unknown>,
): AppointmentScheduledMessageActions {
  const { fetchWithAuth } = useApp();
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const sendNow = async (msg: AppointmentScheduledMessage) => {
    setActionLoading(msg.uuid);
    const name = getScheduledMessageDisplayName(msg);
    try {
      const url = getApiUrl("APPOINTMENT_SCHEDULED_MESSAGE_SEND", "", {
        transactionUuid: msg.transaction_uuid,
        messageUuid: msg.uuid,
      });
      // A 2xx does NOT guarantee delivery: the consumer's compliance re-check
      // can land the row SKIPPED, and the send itself can land FAILED. Surface
      // the final status rather than a blanket success toast.
      const result: SendNowResult | undefined = await fetchWithAuth(url, {
        method: "POST",
      });
      const status = result?.status;
      if (status === "SENT") {
        notifySuccess(`"${name}" sent successfully.`, {
          title: "Message Sent",
        });
      } else if (status === "SKIPPED") {
        notifyWarning(
          result?.failure_reason || `"${name}" was skipped at send time.`,
          { title: "Not Sent" },
        );
      } else if (status === "FAILED") {
        notifyError(result?.failure_reason || `Could not send "${name}".`, {
          title: "Send Failed",
        });
      } else {
        notifyInfo(`"${name}" is being sent.`, { title: "Sending" });
      }
      await onChanged();
    } catch (err: any) {
      notifyError(err?.errorBody?.message ?? "Could not send the message.", {
        title: "Send Failed",
      });
    } finally {
      setActionLoading(null);
    }
  };

  const retry = async (msg: AppointmentScheduledMessage) => {
    setActionLoading(msg.uuid);
    const name = getScheduledMessageDisplayName(msg);
    try {
      const url = getApiUrl("APPOINTMENT_SCHEDULED_MESSAGE_RETRY", "", {
        transactionUuid: msg.transaction_uuid,
        messageUuid: msg.uuid,
      });
      await fetchWithAuth(url, { method: "POST" });
      notifySuccess(`"${name}" rescheduled.`, { title: "Retry Scheduled" });
      await onChanged();
    } catch (err: any) {
      notifyError(err?.errorBody?.message ?? "Could not retry the message.", {
        title: "Retry Failed",
      });
    } finally {
      setActionLoading(null);
    }
  };

  const cancel = async (msg: AppointmentScheduledMessage) => {
    setActionLoading(msg.uuid);
    const name = getScheduledMessageDisplayName(msg);
    try {
      const url = getApiUrl("APPOINTMENT_SCHEDULED_MESSAGE_CANCEL", "", {
        transactionUuid: msg.transaction_uuid,
        messageUuid: msg.uuid,
      });
      await fetchWithAuth(url, { method: "DELETE" });
      notifySuccess(`"${name}" cancelled.`, { title: "Reminder Cancelled" });
      await onChanged();
    } catch (err: any) {
      notifyError(err?.errorBody?.message ?? "Could not cancel the reminder.", {
        title: "Cancel Failed",
      });
    } finally {
      setActionLoading(null);
    }
  };

  return { actionLoading, sendNow, cancel, retry };
}
